import { productCostHistoryQuerySchema, type ProductCostIdentitiesResponse } from "@nova-ai/shared"

import {
  useProductCostHistory,
  useProductCostIdentities,
} from "../../services/use-product-cost-history"
import { QueryState } from "../layout/query-state"
import { Button } from "../ui/button"
import { Combobox } from "../ui/combobox"
import { FormField } from "../ui/form-field"
import { TextInput } from "../ui/text-input"
import { ProductCostCoverage, ProductCostStartingData } from "./product-cost-coverage"
import { ProductCostObservations } from "./product-cost-observations"

export type ProductCostFilters = { store?: string; simpleSku?: string; from?: string; to?: string }
type Props = {
  sku: string
  filters: ProductCostFilters
  onChange: (filters: ProductCostFilters) => void
}

export function ProductCostHistory({ sku, filters, onChange }: Props) {
  const identities = useProductCostIdentities(sku)
  return (
    <section className="space-y-4" aria-label="Product cost history">
      <div>
        <h2 className="text-[15px] font-semibold">Cost history</h2>

        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Select a store and sellable variant. Observations show what Nova received; dates use UTC.
        </p>
      </div>

      <ProductCostStartingData />

      <QueryState
        isLoading={identities.isLoading}
        error={identities.error}
        isEmpty={identities.data?.items.length === 0}
        emptyTitle="No cost identities found"
        emptyMessage="This SKU has no current or historical cost identity. Open an exact simple SKU to inspect its history."
      >
        {identities.data ? (
          <CostIdentityFilters identities={identities.data} filters={filters} onChange={onChange} />
        ) : null}
      </QueryState>

      {filters.simpleSku && filters.store ? (
        <CostTimeline filters={filters} />
      ) : (
        <p className="text-sm text-muted-foreground">
          Choose both a store and a variant to load observations.
        </p>
      )}
    </section>
  )
}

function CostIdentityFilters({
  identities,
  filters,
  onChange,
}: {
  identities: ProductCostIdentitiesResponse
  filters: ProductCostFilters
  onChange: Props["onChange"]
}) {
  const stores = [...new Set(identities.items.map((item) => item.store))].sort()
  const variants = identities.items.filter((item) => item.store === filters.store)
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <FormField id="cost-store" label="Store">
          {(control) => (
            <Combobox
              {...control}
              id="cost-store"
              options={stores.map((store) => ({ value: store, label: store }))}
              value={filters.store}
              placeholder="Choose store"
              onChange={(store) =>
                onChange({
                  ...(store ? { store } : {}),
                  ...(filters.from ? { from: filters.from } : {}),
                  ...(filters.to ? { to: filters.to } : {}),
                })
              }
            />
          )}
        </FormField>

        <FormField id="cost-variant" label="Variant (simple SKU)">
          {(control) => (
            <Combobox
              {...control}
              id="cost-variant"
              disabled={!filters.store}
              options={variants.map((item) => ({
                value: item.simpleSku,
                label: `${item.simpleSku}${item.catalogPresent ? "" : " · historical"}`,
              }))}
              value={filters.simpleSku}
              placeholder="Choose variant"
              onChange={(simpleSku) =>
                onChange({ ...filters, ...(simpleSku ? { simpleSku } : {}) })
              }
            />
          )}
        </FormField>

        <FormField id="cost-from" label="From (UTC)">
          {(control) => (
            <TextInput
              {...control}
              id="cost-from"
              type="date"
              value={filters.from ?? ""}
              onChange={(event) => onChange({ ...filters, from: event.target.value })}
            />
          )}
        </FormField>

        <FormField id="cost-to" label="To (UTC, inclusive)">
          {(control) => (
            <TextInput
              {...control}
              id="cost-to"
              type="date"
              value={filters.to ?? ""}
              onChange={(event) => onChange({ ...filters, to: event.target.value })}
            />
          )}
        </FormField>
      </div>

      {identities.truncated ? (
        <p className="text-sm text-muted-foreground">
          Only the first 500 identities are shown. Open an exact simple SKU for missing variants.
        </p>
      ) : null}
    </div>
  )
}

function CostTimeline({ filters }: { filters: ProductCostFilters }) {
  const from = filters.from === "" ? undefined : filters.from
  const to = filters.to === "" ? undefined : filters.to
  const history = useProductCostHistory(filters.simpleSku ?? "", filters.store ?? "", from, to)
  const valid = productCostHistoryQuerySchema.safeParse({ store: filters.store, from, to })
  if (!valid.success) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Use valid UTC dates. The start must not follow the end.
      </p>
    )
  }
  const first = history.data?.pages[0]
  if (history.error && !first) {
    return (
      <div className="space-y-3">
        <QueryState isLoading={false} error={history.error}>
          {null}
        </QueryState>

        <Button
          onClick={() => {
            void history.refetch()
          }}
        >
          Retry history
        </Button>
      </div>
    )
  }
  return (
    <QueryState isLoading={history.isLoading} error={null}>
      {first ? (
        <div className="space-y-4">
          <ProductCostCoverage coverage={first.coverage} />

          <ProductCostObservations
            items={history.data?.pages.flatMap((page) => page.items) ?? []}
          />

          {history.error ? (
            <p role="alert" className="text-sm text-destructive">
              Could not load more observations. Try again.
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            {history.hasNextPage ? (
              <Button
                disabled={history.isFetchingNextPage}
                onClick={() => {
                  void history.fetchNextPage()
                }}
              >
                {history.isFetchingNextPage ? "Loading observations…" : "Load older observations"}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                End of recorded observations for these dates.
              </span>
            )}

            <Button
              variant="ghost"
              disabled={history.isFetching}
              onClick={() => {
                void history.refetch()
              }}
            >
              Refresh observations
            </Button>
          </div>
        </div>
      ) : null}
    </QueryState>
  )
}
